// ============================================================
// Die Reiterleiste am unteren Rand — nur auf dem Telefon (E20,
// .design/screens.mjs `tabbar`): 62 px hoch, 14 px vom Rand, Radius 22, Glas.
//
// Sie zeigt SECHS Ziele. Die Büroseite fehlt weiterhin mit Absicht — man
// erreicht sie über jedes Projekt und über das Team —; „Aktuelles" (§16.4)
// ist seit dem 19.09. dabei, weil Feed und Forum vor allem auf dem Telefon
// gelesen werden, „Start" seit §32 (28.09.), weil „/" seitdem die Startseite
// ist und die Karte unter `/karte` liegt. Die Felder teilen sich dafür den
// Platz, statt starr 72 px breit zu sein (styles/app.css erklärt, warum):
// bei 390 px rund 57 px je Feld, bei 320 px rund 47. „Team" herauszunehmen
// war die Alternative; §32 (E32.1) behält es.
//
// Welches Feld leuchtet, entscheidet `reiterAn` (lib/wege.ts), nicht
// `NavLink`: auf `/projekte/:id` ist es „Karte" und nicht „Projekte", und
// `aria-current` muss dasselbe sagen wie das Bild.
//
// §36.8: „Start" trägt denselben Punkt wie in der Kopfleiste (`ReiterPunkt`),
// sobald eine Freigabe etwas von mir verlangt — seit dem 29.09.2026 nur eine
// Einreichung, deren Projektleitung ich bin, oder eine eigene, abgelehnte. Die Abfrage teilt sich den
// Zwischenspeicher mit der Kopfleiste; einen zweiten Abruf kostet sie nicht.
//
// „Aktuelles" trägt ebenso den Ungelesen-Punkt der Kopfleiste (§16.4) — seit
// der Fehlerrunde vom 01.10.2026 (#36). Vorher hing er nur am Reiter oben, der
// unter 860 px ausgeblendet ist: am Telefon blieb Neues unbemerkt.
//
// Die Komponente entscheidet SELBST, ob sie erscheint: `useMobil()` statt
// einer CSS-Regel, damit die Karte (F2) ihre Bodenblatt-Höhe aus derselben
// Wahrheit rechnen kann und nicht aus einem Element, das zwar im Baum steht,
// aber unsichtbar ist.
// ============================================================
import { Link, useLocation } from "react-router-dom";
import { Icon } from "../ui";
import { freigabenZahl, useFreigaben } from "../../api/freigaben";
import { useMobil } from "../../hooks/useMedienabfrage";
import { useT } from "../../lib/texte";
import { KARTE, karteVorladen, reiterAn } from "../../lib/wege";
import { useAuthStore } from "../../store/useAuthStore";
import { ReiterPunkt, useAktuellesUngelesen } from "./TopNav";

export function MobilLeiste() {
  const mobil = useMobil();
  const { t } = useT();
  const { pathname } = useLocation();
  // Vor dem frühen Ausstieg: Haken laufen bei jedem Zeichnen in derselben
  // Reihenfolge, auch am Schreibtisch, wo die Leiste nichts zeichnet. Die Leiste
  // steht nur für Angemeldete (App.tsx, Shell unter `Geschuetzt`) — gefragt wird
  // trotzdem nur mit Sitzung, falls sie einmal woanders eingehängt wird.
  const angemeldet = useAuthStore((s) => s.status === "angemeldet");
  const { data: freigaben } = useFreigaben(angemeldet);
  const freigabenN = freigabenZahl(freigaben);
  // Ebenfalls VOR dem frühen Ausstieg — dieselbe Reihenfolge der Haken.
  const ungelesen = useAktuellesUngelesen(angemeldet);
  if (!mobil) return null;

  // `hinweis`: was der Punkt sagt — für Vorleser im Namen des Reiters (wie oben).
  const punkte: { zu: string; text: string; icon: string; genau?: boolean; punkt?: boolean; hinweis?: string }[] = [
    {
      zu: "/",
      text: t.nav.start,
      icon: "house",
      genau: true,
      punkt: freigabenN > 0,
      hinweis: freigabenN > 0 ? t.freigabe.punkt(freigabenN) : undefined,
    },
    { zu: KARTE, text: t.nav.karte, icon: "map" },
    { zu: "/projekte", text: t.nav.projekte, icon: "squares-four" },
    { zu: "/abgaben", text: t.nav.abgaben, icon: "calendar" },
    { zu: "/team", text: t.nav.team, icon: "users" },
    {
      zu: "/aktuelles",
      text: t.nav.aktuelles,
      icon: "bell",
      punkt: ungelesen,
      hinweis: ungelesen ? t.nav.aktuellesUngelesen : undefined,
    },
  ];

  return (
    <nav className="mobil-leiste glass-strong" aria-label={t.nav.bereiche}>
      {punkte.map((p) => {
        const an = reiterAn(p.zu, pathname, p.genau);
        // Beim ersten Berühren von „Karte" schon das Kartenbündel holen — der
        // Finger liegt noch auf dem Glas, wenn der Abruf beginnt.
        const vorladen = p.zu === KARTE ? karteVorladen : undefined;
        return (
          <Link
            key={p.zu}
            to={p.zu}
            aria-label={p.hinweis ? `${p.text}, ${p.hinweis}` : undefined}
            aria-current={an ? "page" : undefined}
            className={`ring-focus${an ? " aktiv" : ""}`}
            onPointerEnter={vorladen}
            onFocus={vorladen}
          >
            <span style={{ position: "relative", display: "inline-flex" }}>
              <Icon name={p.icon} size={21} />
              {p.punkt && <ReiterPunkt />}
            </span>
            {p.text}
          </Link>
        );
      })}
    </nav>
  );
}
